import {
  getStoredIdentity,
  isProtectionEnabled,
} from './identity';
import { getUnlockedPrivateKey } from './sessionUnlockedKey';
import { idbGet, idbSet } from './indexedDbStorage';
import { getStoredRelays } from './relay';
import { getStoredPairs } from './pairing';
import { getStoredKeyPair, getStoredServices } from './storage';
import type { LocalIdentity, RelayConfig, PairConfig } from '../types/identity';
import type { KeyPair } from './crypto';
import type { ServiceConfig } from '../types/auth';
import type { PairingConfirmedRecord } from '../services/pairingConfirm';

const IDB_KEY_PAIRING_CONFIRM = 'userwallet_pairing_confirm';

const STORAGE_KEY_IDENTITY = 'userwallet_identity';
const STORAGE_KEY_RELAYS = 'userwallet_relays';
const STORAGE_KEY_PAIRS = 'userwallet_pairs';
const STORAGE_KEY_HASH_CACHE = 'userwallet_hash_cache';
const STORAGE_KEY_KEYPAIR = 'userwallet_keypair';
const STORAGE_KEY_SERVICES = 'userwallet_services';

export const EXPORT_VERSION = '1.0';

/**
 * Trigger download of export JSON. Used by DataExportImportScreen and HomeScreen (pre-delete export).
 */
export function downloadExportFile(json: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `userwallet-export-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface UserWalletExport {
  version: string;
  exportedAt: number;
  identity: LocalIdentity | null;
  relays: RelayConfig[];
  pairs: PairConfig[];
  hash_cache: string[];
  pairing_confirm?: PairingConfirmedRecord[];
  keypair?: KeyPair | null;
  services?: ServiceConfig[];
}

/**
 * Export all UserWallet data as JSON string.
 * Identity, relays, pairs, keypair, services from localStorage; hash_cache from IndexedDB.
 * When protection is enabled, requires unlock (session key) to include privateKey.
 */
export async function exportUserWalletData(): Promise<string> {
  let identity = getStoredIdentity();
  if (
    identity !== null &&
    isProtectionEnabled() &&
    identity.privateKey === undefined
  ) {
    const sessionKey = getUnlockedPrivateKey();
    if (sessionKey === null) {
      throw new Error('Déverrouillez l\'identité pour exporter la clé privée.');
    }
    identity = { ...identity, privateKey: sessionKey };
  }
  const relays = getStoredRelays();
  const pairs = getStoredPairs();
  const keypair = getStoredKeyPair();
  const services = getStoredServices();
  const hashCacheRaw = await idbGet(STORAGE_KEY_HASH_CACHE);
  const hash_cache: string[] =
    hashCacheRaw !== null ? (JSON.parse(hashCacheRaw) as string[]) : [];

  const pairingConfirmRaw = await idbGet(IDB_KEY_PAIRING_CONFIRM);
  let pairing_confirm: PairingConfirmedRecord[] = [];
  if (pairingConfirmRaw !== null) {
    try {
      const parsed = JSON.parse(pairingConfirmRaw) as PairingConfirmedRecord[];
      pairing_confirm = Array.isArray(parsed) ? parsed : [];
    } catch {
      pairing_confirm = [];
    }
  }

  const data: UserWalletExport = {
    version: EXPORT_VERSION,
    exportedAt: Date.now(),
    identity,
    relays,
    pairs,
    hash_cache,
    pairing_confirm,
    keypair: keypair ?? null,
    services,
  };
  return JSON.stringify(data, null, 2);
}

function isRelayConfig(x: unknown): x is RelayConfig {
  if (x === null || typeof x !== 'object') {
    return false;
  }
  const o = x as Record<string, unknown>;
  return (
    typeof o.endpoint === 'string' &&
    typeof o.priority === 'number' &&
    typeof o.enabled === 'boolean'
  );
}

function isPairConfig(x: unknown): x is PairConfig {
  if (x === null || typeof x !== 'object') {
    return false;
  }
  const o = x as Record<string, unknown>;
  return (
    typeof o.uuid === 'string' &&
    Array.isArray(o.membres_parents_uuid) &&
    typeof o.is_local === 'boolean' &&
    typeof o.can_sign === 'boolean'
  );
}

function isPairingConfirmedRecord(x: unknown): x is PairingConfirmedRecord {
  if (x === null || typeof x !== 'object') {
    return false;
  }
  const o = x as Record<string, unknown>;
  return (
    typeof o.pairLocal === 'string' &&
    typeof o.pairRemote === 'string' &&
    typeof o.hash === 'string' &&
    typeof o.version === 'number'
  );
}

function isLocalIdentity(x: unknown): x is LocalIdentity {
  if (x === null || typeof x !== 'object') {
    return false;
  }
  const o = x as Record<string, unknown>;
  return (
    typeof o.uuid === 'string' &&
    typeof o.privateKey === 'string' &&
    typeof o.publicKey === 'string' &&
    typeof o.t0_anniversaire === 'number' &&
    typeof o.version === 'string'
  );
}

/**
 * Import UserWallet data from JSON string.
 * Overwrites existing data. identity, relays, pairs, keypair, services → localStorage;
 * hash_cache → IndexedDB. Throws on invalid format.
 */
export async function importUserWalletData(json: string): Promise<void> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json) as unknown;
  } catch (err) {
    throw new Error('Invalid JSON');
  }
  if (parsed === null || typeof parsed !== 'object') {
    throw new Error('Export must be an object');
  }
  const o = parsed as Record<string, unknown>;
  if (typeof o.version !== 'string' || typeof o.exportedAt !== 'number') {
    throw new Error('Missing or invalid version/exportedAt');
  }
  if (!Array.isArray(o.relays) || !o.relays.every(isRelayConfig)) {
    throw new Error('Invalid relays');
  }
  if (!Array.isArray(o.pairs) || !o.pairs.every(isPairConfig)) {
    throw new Error('Invalid pairs');
  }
  if (!Array.isArray(o.hash_cache) || !o.hash_cache.every((h) => typeof h === 'string')) {
    throw new Error('Invalid hash_cache');
  }

  if (o.identity !== null && !isLocalIdentity(o.identity)) {
    throw new Error('Invalid identity');
  }

  if (o.identity === null) {
    localStorage.removeItem(STORAGE_KEY_IDENTITY);
  } else {
    localStorage.setItem(STORAGE_KEY_IDENTITY, JSON.stringify(o.identity));
  }
  localStorage.setItem(STORAGE_KEY_RELAYS, JSON.stringify(o.relays));
  localStorage.setItem(STORAGE_KEY_PAIRS, JSON.stringify(o.pairs));
  await idbSet(STORAGE_KEY_HASH_CACHE, JSON.stringify(o.hash_cache));

  if ('keypair' in o && o.keypair !== undefined) {
    if (o.keypair === null) {
      localStorage.removeItem(STORAGE_KEY_KEYPAIR);
    } else {
      localStorage.setItem(STORAGE_KEY_KEYPAIR, JSON.stringify(o.keypair));
    }
  }
  if ('services' in o && o.services !== undefined && Array.isArray(o.services)) {
    localStorage.setItem(STORAGE_KEY_SERVICES, JSON.stringify(o.services));
  }
  if (
    'pairing_confirm' in o &&
    o.pairing_confirm !== undefined &&
    Array.isArray(o.pairing_confirm) &&
    o.pairing_confirm.every(isPairingConfirmedRecord)
  ) {
    await idbSet(IDB_KEY_PAIRING_CONFIRM, JSON.stringify(o.pairing_confirm));
  }
}
